import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { RouteComponentProps, withRouter } from "react-router-dom";

import * as analytics from "../../analytics";
import { OrderSummary } from "../../components/organisms";
import {
  BookingLocationPresenter,
  inflateBookingLocationPresenter,
} from "../../presenters/booking-location";
import { IAppConfiguration } from "../../services/api/configuration";
import { IPromotion } from "../../services/api/promotion";
import { IServiceTreatment, ITherapist } from "../../services/api/therapists";
import {
  actionCreators as checkoutActions,
  clearPromoAndRecalculate,
  getCreditBalance,
  promocodeValidation,
} from "../../store/checkout/actions";
import { IRootState } from "../../store/index";
import { actionCreators as treatmentsActions } from "../../store/treatments/actions";
import { AddressType } from "../../store/postcodes/reducer";
import { normalizeObjects } from "../../store/utils";
import {
  intercomPosition,
  intercomPositionOnModalOpen,
  isMobileWidth,
} from "../../utils/index";

interface IProps {
  isCheckLoading?: boolean;
  onCheckout?(): void;
}

interface IState {
  isMobile: boolean;
  isMobileSummaryOpen: boolean;
}

interface IReduxProps {
  bookingLocation?: BookingLocationPresenter;
  billingLocation?: BookingLocationPresenter;
  configuration?: IAppConfiguration;
  creditBalance?: number;
  creditSubBalance?: number;
  date?: string;
  isLoggedIn: boolean;
  originalAmount?: number;
  promocode?: IPromotion;
  therapist?: ITherapist;
  bookingFee: number;
  isBookingFeeLoading: boolean;
  totalAmount?: number;
  treatments: { [urn: string]: IServiceTreatment };
}

type Props = IProps &
  IReduxProps &
  RouteComponentProps & { dispatch: Dispatch<any> };

class OrderSummaryContainer extends React.Component<Props, IState> {
  private readonly mobileCardRef = React.createRef<HTMLElement>();

  public state = {
    isMobile: false,
    isMobileSummaryOpen: false,
  };

  /**
   * Toggle mobile view
   */
  private readonly toggleIsMobile = (isMobile: boolean): void => {
    isMobile
      ? this.setState({ isMobile: true }, () => {
          intercomPosition(true);
        })
      : this.setState({ isMobile: false }, () => {
          intercomPosition(false);
        });
  };

  /**
   * Returns address string
   */
  private readonly getAddressString = (
    addressLocation: BookingLocationPresenter | undefined,
  ): string | undefined => {
    if (addressLocation && addressLocation.canCheckoutWithThisAddress()) {
      return `${addressLocation.getTitle()}, ${addressLocation.getLabel()}`;
    }
  };

  public componentDidMount() {
    if (this.props.isLoggedIn && this.props.creditBalance === undefined) {
      this.props.dispatch(getCreditBalance());
    }

    // initial checks for mobile view
    isMobileWidth(this.toggleIsMobile);
    this.checkOffset();

    window.addEventListener("resize", () => isMobileWidth(this.toggleIsMobile));
    window.addEventListener("scroll", this.checkOffset);

    this.calculateOriginalAmount();

    if (this.props.promocode && this.props.promocode.isValid) {
      this.props.dispatch(
        promocodeValidation(
          this.props.promocode.code,
          normalizeObjects(this.props.treatments).map(
            (treatment: IServiceTreatment) => treatment.urn,
          ),
        ),
      );
    }
  }

  public componentDidUpdate(): void {
    this.calculateOriginalAmount();
  }

  public componentWillUnmount(): void {
    if (this.state.isMobile) {
      intercomPosition(true);
    }

    window.removeEventListener("resize", () =>
      isMobileWidth(this.toggleIsMobile),
    );
    window.removeEventListener("scroll", this.checkOffset);
  }

  public calculateOriginalAmount(): void {
    const countTotalPayment =
    normalizeObjects(this.props.treatments).length > 0
      ? normalizeObjects(this.props.treatments)
          .map(
            (treatment: IServiceTreatment) =>
              treatment.price * (treatment.count || 0),
          )
          .reduce((accumulator, currentValue) => accumulator + currentValue)
      : 0;
    
    if (this.props.originalAmount !== countTotalPayment) {
      this.props.dispatch(checkoutActions.setOriginalAmount(countTotalPayment));
    }
  }

  public checkOffset = () => {
    if (!this.state.isMobile) {
      return;
    }

    const windowScrollY = window.scrollY + window.innerHeight;
    const footer = document.getElementById("footer");
    if (footer) {
      const footerOffSet = footer.offsetTop;
      const mobileModal = this.mobileCardRef.current;

      if (mobileModal) {
        if (windowScrollY < footerOffSet) {
          mobileModal.style.bottom = "0";
        }
        if (windowScrollY >= footerOffSet) {
          mobileModal.style.bottom = `${windowScrollY - footerOffSet}px`;
        }
      }
    }
  };

  /**
   * Clear promo code
   */
  public removePromocode = () => {
    this.props.dispatch(clearPromoAndRecalculate());
  };

  /**
   * toggle mobile or full order summary component
   */
  public toggleMobileSummary = () => {
    if (this.state.isMobileSummaryOpen) {
      this.setState({ isMobileSummaryOpen: false }, () => {
        intercomPositionOnModalOpen(false);
      });
    } else {
      this.setState({ isMobileSummaryOpen: true }, () => {
        intercomPositionOnModalOpen(true);
      });
    }
  };

  /**
   * Add treatment
   */
  public addTreatment = (service: IServiceTreatment): void => {
    this.props.dispatch(treatmentsActions.addTreatment(service));

    analytics.trackAddTreatment(service);
  };

  /**
   * Remove treatment
   */
  public removeTreatment = (urn: string): void => {
    this.props.dispatch(treatmentsActions.removeTreatment(urn));

    analytics.track({
      name: analytics.AnalyticsEvents.TREATMENT_REMOVE,
      payload: {
        label: urn,
      },
    });
  };

  public changeAddress = (addressType: AddressType) => {
    const path = this.props.match.path;

    if (path.startsWith("/address")) {
      this.props.history.replace(`/address/${addressType}/select`);
    } else {
      this.props.history.push(`/address/${addressType}/select`);
    }
  };

  /**
   * Render order summary component
   */
  public render() {
    return (
      <OrderSummary
        bookingLocation={this.props.bookingLocation}
        bookingAddressString={this.getAddressString(this.props.bookingLocation)}
        billingLocation={this.props.billingLocation}
        billingAddressString={this.getAddressString(this.props.billingLocation)}
        creditBalance={this.props.creditBalance}
        creditSubBalance={this.props.creditSubBalance}
        date={this.props.date}
        isCheckLoading={this.props.isCheckLoading}
        onCheckout={this.props.onCheckout}
        originalAmount={this.props.originalAmount}
        promocode={this.props.promocode}
        removePromo={this.removePromocode}
        onAdd={this.addTreatment}
        onRemove={this.removeTreatment}
        therapist={this.props.therapist}
        bookingFee={this.props.bookingFee}
        isBookingFeeLoading={this.props.isBookingFeeLoading}        
        totalAmount={this.props.totalAmount}
        treatments={normalizeObjects(this.props.treatments)}
        changeAddress={this.changeAddress}
        isLoggedIn={this.props.isLoggedIn}
      />
    );
  }
}

// store
const mapStateToProps = (state: IRootState): IReduxProps => {
  return {
    ...(state.postcodesState.bookingLocation && {
      bookingLocation: inflateBookingLocationPresenter(
        state.postcodesState.bookingLocation,
      ),
    }),
    ...(state.postcodesState.billingLocation && {
      billingLocation: inflateBookingLocationPresenter(
        state.postcodesState.billingLocation,
      ),
    }),
    creditBalance: state.checkoutState.creditBalance,
    creditSubBalance: state.checkoutState.creditSubBalance,
    configuration: state.configurationState.configuration,
    date: state.therapistsState.selectedDate,
    isLoggedIn: state.userState.isLogedIn,
    originalAmount: state.checkoutState.originalAmount,
    promocode: state.checkoutState.promocode,
    therapist: state.therapistsState.selectedTherapist,
    bookingFee: state.checkoutState.bookingFee,
    isBookingFeeLoading: state.checkoutState.isBookingFeeLoading,
    totalAmount: state.checkoutState.totalAmount,
    treatments: state.treatmentsState.treatments,
  };
};

const store = withRouter(
  connect<IReduxProps, {}, {}, IRootState>(mapStateToProps)(
    OrderSummaryContainer,
  ),
);

export { store as OrderSummaryContainer };
